import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Grid, Paper, Snackbar, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AnimatedPage,
  AnimatedSection,
  metricMotion,
} from '../components/animations/MotionContainers';
import { EntradaStatCard } from '../components/entradas/EntradaStatCard';
import { EntradaHistorial } from '../components/entradas/EntradaHistorial';
import { useFlujosCatalogs } from '../hooks/useFlujosCatalogs';
import { useFlujosEntrada } from '../hooks/useFlujosEntradas';
import { formatDisplayDate } from '../utils/dashboardData';
import { downloadEntradaPreviewPdf } from '../utils/entradaPreviewPdf';
import { calculateEntradaTotals, formatKilos, formatPercent } from '../utils/flujoCalculations';

const DetailField = ({ label, value }) => (
  <Box
    sx={{
      p: 1.5,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: 'background.paper',
      minHeight: 70,
    }}
  >
    <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 700, mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 16, fontWeight: 900 }}>
      {value || '-'}
    </Typography>
  </Box>
);

const DetailSection = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.5, sm: 2 },
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
    }}
  >
    <Typography sx={{ color: 'primary.main', fontSize: 16, fontWeight: 900, mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const ClassificationField = ({ label, kilos, palots }) => (
  <Box
    sx={{
      p: 1.5,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: 'background.paper',
      minHeight: 78,
    }}
  >
    <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 700, mb: 1 }}>
      {label}
    </Typography>
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-end', justifyContent: 'space-between' }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 700 }}>
          Kilos
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 900 }}>
          {formatKilos(kilos)} kg
        </Typography>
      </Box>
      {palots !== undefined && (
        <Box sx={{ minWidth: 0, textAlign: 'right' }}>
          <Typography sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 700 }}>
            Palots
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 900 }}>
            {formatKilos(palots)}
          </Typography>
        </Box>
      )}
    </Stack>
  </Box>
);

export const ConsultaEntradaDetalle = () => {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const {
    almacenes,
    variedades,
    cajasPalet,
    pesoPalotMermaDefault,
    loading: catalogsLoading,
    error: catalogsError,
  } = useFlujosCatalogs();
  const { entrada, loading, error } = useFlujosEntrada(codigo, { almacenes, variedades, cajasPalet });
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState('');
  const totals = useMemo(
    () => entrada
      ? calculateEntradaTotals(entrada, cajasPalet, { pesoPalotMerma: pesoPalotMermaDefault })
      : null,
    [cajasPalet, entrada, pesoPalotMermaDefault]
  );

  const handlePrintEntrada = async () => {
    setPrinting(true);
    setPrintError('');

    try {
      await downloadEntradaPreviewPdf({ entrada, totals, almacenes, variedades, cajasPalet });
    } catch (printPdfError) {
      setPrintError(printPdfError?.message || 'No se ha podido generar el PDF.');
    } finally {
      setPrinting(false);
    }
  };

  if (loading || catalogsLoading || error || catalogsError || !entrada) {
    return (
      <AnimatedPage>
        <Stack spacing={2.5}>
          <Alert severity={error || catalogsError ? 'error' : 'info'} variant="outlined">
            {error || catalogsError || 'Cargando entrada...'}
          </Alert>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/consulta-entradas')}
            sx={{ alignSelf: 'flex-start' }}
          >
            Volver
          </Button>
        </Stack>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage>
        <Stack spacing={2.5}>
          <AnimatedSection>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                  }}
                >
                  <FactCheckOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h1">Entrada #{entrada.codigo}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Resumen detallado de entrada registrada
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}>
                <Button
                  variant="contained"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => navigate(`/consulta-entradas/${codigo}/editar`)}
                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                >
                  Editar
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintOutlinedIcon />}
                  onClick={handlePrintEntrada}
                  disabled={printing}
                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                >
                  {printing ? 'Generando...' : 'Imprimir'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => navigate('/consulta-entradas')}
                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                >
                  Volver
                </Button>
              </Stack>
            </Stack>
          </AnimatedSection>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
              <AnimatedSection variant={metricMotion}>
                <EntradaStatCard
                  icon={<ScaleOutlinedIcon />}
                  title="Kg volcados"
                  value={formatKilos(entrada.kilosVolcados)}
                  suffix="kg"
                />
              </AnimatedSection>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
              <AnimatedSection variant={metricMotion}>
                <EntradaStatCard
                  icon={<ScaleOutlinedIcon />}
                  title="Kg reales"
                  value={formatKilos(entrada.kgReales)}
                  suffix="kg"
                />
              </AnimatedSection>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
              <AnimatedSection variant={metricMotion}>
                <EntradaStatCard
                  icon={<Inventory2OutlinedIcon />}
                  title="Kg clasificados"
                  value={formatKilos(entrada.kilosClasificados)}
                  suffix="kg"
                />
              </AnimatedSection>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
              <AnimatedSection variant={metricMotion}>
                <EntradaStatCard
                  icon={<PercentOutlinedIcon />}
                  title="Rendimiento"
                  value={formatPercent(entrada.rendimiento)}
                  suffix="%"
                />
              </AnimatedSection>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
              <AnimatedSection variant={metricMotion}>
                <EntradaStatCard
                  icon={<TrendingUpOutlinedIcon />}
                  title="% primera"
                  value={formatPercent(entrada.porcentajePrimera)}
                  suffix="%"
                />
              </AnimatedSection>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <AnimatedSection>
                <DetailSection title="Datos generales">
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DetailField label="Codigo" value={`#${entrada.codigo}`} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DetailField label="Fecha" value={formatDisplayDate(entrada.fecha)} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <DetailField label="Variedad" value={entrada.variedad} />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <DetailField label="Total palots" value={formatKilos(entrada.totalPalots)} />
                    </Grid>
                  </Grid>
                </DetailSection>
              </AnimatedSection>
            </Grid>

            <Grid size={{ xs: 12, lg: 8 }}>
              <AnimatedSection>
                <DetailSection title="Kilos volcados">
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <DetailField label="Kilos" value={`${formatKilos(entrada.kilos)} kg`} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <DetailField label="Grupos de 4 palots" value={formatKilos(entrada.crucesPalots)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <DetailField label="Peso del palot" value={`${formatKilos(entrada.kilosPalot)} kg`} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <DetailField label="Peso caja" value={`${formatKilos(entrada.pesoCaja)} kg`} />
                    </Grid>
                    {cajasPalet.map((cajaPalet) => (
                      <Grid key={cajaPalet.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <DetailField
                          label={`Palets de ${cajaPalet.cajas} cajas`}
                          value={formatKilos(entrada.paletsEntrada?.[cajaPalet.id] ?? 0)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </DetailSection>
              </AnimatedSection>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <AnimatedSection>
                <DetailSection title="Clasificacion">
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                      <ClassificationField
                        label="Precalibrado"
                        kilos={entrada.otrosCalibres}
                        palots={entrada.pOtrosCalibres}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                      <ClassificationField
                        label="Retorno"
                        kilos={entrada.retorno}
                        palots={entrada.pRetorno}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                      <ClassificationField
                        label="Verde"
                        kilos={entrada.verde}
                        palots={entrada.pVerde}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                      <ClassificationField
                        label="Segunda"
                        kilos={entrada.segunda}
                        palots={entrada.pSegunda}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                      <ClassificationField
                        label="Bajo calibre"
                        kilos={entrada.bajoCalibre}
                        palots={entrada.pBajoCalibre}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                      <ClassificationField
                        label="Industria"
                        kilos={entrada.industria}
                        palots={entrada.pIndustria}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                      <ClassificationField label="Podrido" kilos={entrada.podrido} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                      <DetailField label="Primera" value={`${formatKilos(entrada.primera)} kg`} />
                    </Grid>
                  </Grid>
                </DetailSection>
              </AnimatedSection>
            </Grid>
          </Grid>

          <AnimatedSection>
            <DetailSection title="Observaciones">
              <Typography sx={{ fontSize: 15, lineHeight: '22px' }}>
                {entrada.observaciones || 'Sin observaciones'}
              </Typography>
            </DetailSection>
          </AnimatedSection>
          <AnimatedSection>
            <EntradaHistorial entradaId={entrada.id} />
          </AnimatedSection>
          <Snackbar
            open={Boolean(printError)}
            autoHideDuration={4500}
            onClose={() => setPrintError('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert severity="error" variant="filled" onClose={() => setPrintError('')}>
              {printError}
            </Alert>
          </Snackbar>
        </Stack>
    </AnimatedPage>
  );
};
